import type { EmoteMap, EmoteProvider } from '../api/types.js';

/**
 * Splitting a message into words and emotes (ARCHITECTURE.md §56, §57).
 *
 * ============================ WHY WHOLE WORDS ============================
 * Third-party emotes are not in the provider payload. Twitch sends
 * `catJAM` as three ordinary characters and a client is expected to recognise
 * it — which is why 7TV, BTTV and FFZ all work by matching message TEXT.
 *
 * The match must be on whole, whitespace-delimited words and it must be
 * case-SENSITIVE. `catJAM` and `catjam` are different emotes on 7TV, and a
 * substring match would turn the word "Sadge" inside "Sadgerly" into a
 * picture, silently editing what somebody said.
 *
 * Provider text is still text. This produces SEGMENTS for React to render as
 * elements — nothing here builds markup, so a message named after an emote
 * cannot become one.
 * =======================================================================
 */

export type EmoteSegment =
  | { kind: 'text'; text: string }
  | { kind: 'emote'; name: string; id: string; provider: EmoteProvider };

/**
 * Split on whitespace, keeping it.
 *
 * The separators are preserved as their own segments so the message renders
 * with the spacing the person typed: collapsing runs of spaces would rewrite
 * ASCII art, which chat is full of.
 */
export function segmentEmotes(text: string, emotes: EmoteMap): EmoteSegment[] {
  // No emote map means no work at all — the common path before the fetch
  // settles, and for a workspace with the provider disabled.
  if (Object.keys(emotes).length === 0) return [{ kind: 'text', text }];

  const parts = text.split(/(\s+)/u);
  const segments: EmoteSegment[] = [];

  for (const part of parts) {
    const found = Object.hasOwn(emotes, part) ? emotes[part] : undefined;

    if (found === undefined) {
      // Merged into the previous text run so the DOM gets one node per span of
      // ordinary words rather than one per word.
      const last = segments.at(-1);
      if (last?.kind === 'text') last.text += part;
      else segments.push({ kind: 'text', text: part });
      continue;
    }

    segments.push({ kind: 'emote', name: part, id: found.id, provider: found.provider });
  }

  return segments;
}
